import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronUp, ChevronDown } from 'lucide-react';
import BoardGamePath from './BoardGamePath';

interface BoardGamePathMiniProps {
  total: number;
  current: number;
  screenLabels: string[];
  onNavigateToStep?: (index: number) => void;
}

/** Colors that intensify as the student progresses */
const PROGRESS_COLORS = [
  'hsl(var(--primary) / 0.25)',
  'hsl(var(--primary) / 0.4)',
  'hsl(var(--primary) / 0.55)',
  'hsl(var(--primary) / 0.7)',
  'hsl(var(--primary) / 0.85)',
  'hsl(var(--primary))',
];

function getColor(step: number, total: number): string {
  const idx = Math.min(
    Math.floor((step / Math.max(total - 1, 1)) * (PROGRESS_COLORS.length - 1)),
    PROGRESS_COLORS.length - 1
  );
  return PROGRESS_COLORS[idx];
}

const BoardGamePathMini = ({ total, current, screenLabels, onNavigateToStep }: BoardGamePathMiniProps) => {
  const [expanded, setExpanded] = useState(false);
  const currentLabel = screenLabels[current] || `Step ${current + 1}`;
  const progress = total > 1 ? current / (total - 1) : 0;
  const walkerColor = getColor(current, total);

  return (
    <div className="relative">
      {/* Compact strip with walking figure */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-card/80 backdrop-blur-sm border border-border/40 hover:border-primary/30 transition-all"
        aria-label={expanded ? 'Collapse trail map' : 'Expand trail map'}
      >
        {/* Walking figure SVG */}
        <div className="shrink-0 w-8 h-8">
          <svg viewBox="-8 -10 16 38" className="w-full h-full">
            {/* Head */}
            <motion.circle
              cx={0} cy={2} r={4}
              fill={walkerColor}
              stroke="hsl(var(--primary))"
              strokeWidth={0.8}
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
            />
            {/* Body */}
            <motion.path
              d="M 0 6 L 0 16"
              stroke={walkerColor}
              strokeWidth={2.2}
              strokeLinecap="round"
              fill="none"
            />
            {/* Arms */}
            <motion.path
              d="M -5 10 L 0 11.5 L 5 10"
              stroke={walkerColor}
              strokeWidth={1.8}
              strokeLinecap="round"
              fill="none"
            />
            {/* Legs */}
            <motion.path
              d="M 0 16 L -4 23 M 0 16 L 4 23"
              stroke={walkerColor}
              strokeWidth={1.8}
              strokeLinecap="round"
              fill="none"
            />
          </svg>
        </div>

        {/* Progress trail dots */}
        <div className="flex items-center gap-1 flex-1">
          {Array.from({ length: total }).map((_, i) => (
            <motion.div
              key={i}
              className="rounded-full transition-all"
              style={{
                width: i === current ? 10 : i < current ? 7 : 5,
                height: i === current ? 10 : i < current ? 7 : 5,
                backgroundColor: i <= current ? getColor(i, total) : 'hsl(var(--muted-foreground) / 0.15)',
              }}
              animate={i === current ? { scale: [1, 1.2, 1] } : {}}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          ))}
          {/* Trail line connecting dots */}
          <div
            className="absolute left-14 right-24 h-0.5 rounded-full"
            style={{
              background: `linear-gradient(to right, ${walkerColor} ${progress * 100}%, hsl(var(--muted-foreground) / 0.1) ${progress * 100}%)`,
            }}
          />
        </div>

        {/* Current step label */}
        <span className="text-[10px] text-muted-foreground font-medium whitespace-nowrap shrink-0">
          {current + 1}/{total}
        </span>

        {/* Expand toggle */}
        {expanded ? (
          <ChevronUp className="w-3.5 h-3.5 text-muted-foreground/60 shrink-0" />
        ) : (
          <ChevronDown className="w-3.5 h-3.5 text-muted-foreground/60 shrink-0" />
        )}
      </button>

      {/* Expanded full trail overlay */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden mt-2 rounded-xl bg-card/90 backdrop-blur-md border border-border/40 p-4"
          >
            <BoardGamePath
              total={total}
              current={current}
              screenLabels={screenLabels}
              onNavigateToStep={(index) => {
                onNavigateToStep?.(index);
                setExpanded(false);
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BoardGamePathMini;